import { ReactElement, useState } from 'react';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { BillDetails } from './Components/BillDetails';
import { TableRowLoader } from '../../components/TableRowLoader';
import { FVBill } from '../../lib/interface';
import { Reports } from '../../lib/routes';
import { GetBill } from '../../lib/useDeveloperApi';
import { getAmountView } from '../../utils/format';

const formatGMT8 = (utcString: string): string => {
  const offsetDate = new Date(new Date(utcString).getTime() + 8 * 60 * 60 * 1000);
  return offsetDate.toISOString().substring(0, 16).replace('T', ' ');
};

const NUM_COLUMNS = 10;

function BillExpandedContent({ billId, customerAppId }: { billId: string; customerAppId: string }) {
  const { data, isLoading, error, mutate } = GetBill(customerAppId, billId);

  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={NUM_COLUMNS} align="center" sx={{ py: 1 }}>
          <CircularProgress size={20} />
        </TableCell>
      </TableRow>
    );
  }

  if (error) {
    return (
      <TableRow>
        <TableCell colSpan={NUM_COLUMNS} align="center" sx={{ color: 'error.main', py: 1 }}>
          Failed to load payments
          <Button size="small" onClick={() => mutate()} sx={{ ml: 1 }}>
            Retry
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  const payments = data?.payments ?? [];

  if (payments.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={NUM_COLUMNS} align="center" sx={{ color: 'text.secondary', py: 1 }}>
          No payments
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {payments.map((payment) => {
        const params = new URLSearchParams({ search_text: payment.sender.id });
        const paymentsUrl = `${Reports.Payments}?${params.toString()}`;

        return (
          <TableRow key={payment.payment_id} sx={{ backgroundColor: 'grey.50' }}>
            <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
              {formatGMT8(payment.updated_at)}
            </TableCell>
            <TableCell align="center" sx={{ color: 'text.secondary' }}>
              {payment.status}
            </TableCell>
            <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
              {getAmountView(payment.currency, payment.amount)}
            </TableCell>
            <TableCell align="center" sx={{ color: 'text.secondary' }}>
              {payment.currency}
            </TableCell>
            <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
              <Button
                size="small"
                component="a"
                href={paymentsUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem !important' }} />}
              >
                {payment.payment_id}
              </Button>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}

type BillsTableProps = {
  items: FVBill[];
  isLoading: boolean;
  customerAppId: string;
};

export const BillsTable = ({ items, isLoading, customerAppId }: BillsTableProps): ReactElement => {
  const [openModalId, setOpenModalId] = useState('');
  const [expandedBillId, setExpandedBillId] = useState('');

  const handleModalClose = () => setOpenModalId('');

  const toggleExpand = (billId: string) => {
    setExpandedBillId((prev) => (prev === billId ? '' : billId));
  };

  return (
    <TableContainer component={Paper}>
      {items.map((bill) => (
        <BillDetails
          key={bill.bill_id}
          open={openModalId === bill.bill_id}
          handleClose={handleModalClose}
          bill={bill}
        />
      ))}
      <Table aria-label="bills table">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 600 } }}>
            <TableCell align="center">Updated At (GMT+8)</TableCell>
            <TableCell align="center">Bill ID</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Type</TableCell>
            <TableCell align="center">Amount</TableCell>
            <TableCell align="center">Currency</TableCell>
            <TableCell align="center">Reference</TableCell>
            <TableCell align="center">Payer</TableCell>
            <TableCell align="center">External User ID</TableCell>
            <TableCell align="center">Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRowLoader rows={10} columns={NUM_COLUMNS} />
          ) : (
            items.map((bill) => (
              <>
                <TableRow
                  key={bill.bill_id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => toggleExpand(bill.bill_id)}
                >
                  <TableCell align="center">{formatGMT8(bill.updated_at)}</TableCell>
                  <TableCell align="center">{bill.bill_id}</TableCell>
                  <TableCell align="center">{bill.status}</TableCell>
                  <TableCell align="center">{bill.integration_id}</TableCell>
                  <TableCell align="center">
                    {getAmountView(bill.details.currency, bill.details.total_amount_due)}
                  </TableCell>
                  <TableCell align="center">{bill.details.currency}</TableCell>
                  <TableCell align="center">{bill.details.bill_reference_id}</TableCell>
                  <TableCell align="center">{bill.sender_details.name}</TableCell>
                  <TableCell align="center">{bill.external_user_id}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenModalId(bill.bill_id);
                      }}
                    >
                      {bill.details.description ?? 'Details'}
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow key={`${bill.bill_id}-expanded`}>
                  <TableCell colSpan={NUM_COLUMNS} sx={{ p: 0, borderBottom: 0 }}>
                    <Collapse in={expandedBillId === bill.bill_id} unmountOnExit>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              colSpan={NUM_COLUMNS}
                              align="center"
                              sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}
                            >
                              Payment History
                            </TableCell>
                          </TableRow>
                          <TableRow
                            sx={{
                              '& th': {
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: '0.75rem',
                                backgroundColor: 'grey.50',
                              },
                            }}
                          >
                            <TableCell colSpan={2} align="center">
                              Updated At (GMT+8)
                            </TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell colSpan={2} align="center">
                              Amount
                            </TableCell>
                            <TableCell align="center">Currency</TableCell>
                            <TableCell colSpan={4} align="center">
                              Payment ID
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <BillExpandedContent billId={bill.bill_id} customerAppId={customerAppId} />
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
